import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AppConfig } from '../app.config';
import { CalculationCompound, CalculationCompounds, CalculationCompoundStandatMap, Degradation } from '../contracts';

@Injectable()
export class CompoundService {

    public chloreredeEthener = ['127184', '79016', '156592', '75014'];
    public chloreredeEthaner = ['71556', '75343', '75003'];
    public chloreredeMethaner = ['56235', '67663', '75092', '74873'];
    public glyphosat = ['1071836', '1066519'];
    public dichlobenil = ['1194656', '2008584'];
    public chloridazon = ['1698608', '6339191'];
    public dichlorprop = ['120365', '3307399'];
    public atrazin = ['1912249', '6190654'];
    public mtbe = ['1634044', '75650'];

    constructor(private readonly http: HttpClient) { }

    public getCompounds(): Observable<Array<CalculationCompound>> {
        const url = AppConfig.apiUrls.compounds.list();
        return this.http.get<Array<CalculationCompound>>(url);
    }

    public getCompoundsFromCache(key: string): Array<CalculationCompound> {
        const json = sessionStorage.getItem(key);
        if (!json) {
            return Array<CalculationCompound>();
        }
        return JSON.parse(json) as Array<CalculationCompound>;
    }

    public setCompoundsFromCache(key: string, res: Array<CalculationCompound>): void {
        const json = JSON.stringify(res);
        sessionStorage.setItem(key, json);
    }

    public getScreeningCompounds(): Observable<Array<CalculationCompound>> {
        const url = AppConfig.apiUrls.screeningCompounds.list();
        return this.http.get<Array<CalculationCompound>>(url);
    }

    public getScreeningCompoundsFromCache(key: string): Array<CalculationCompound> {
        const json = sessionStorage.getItem(key);
        if (!json) {
            return Array<CalculationCompound>();
        }
        return JSON.parse(json) as Array<CalculationCompound>;
    }

    public setScreeningCompoundsFromCache(key: string, res: Array<CalculationCompound>): void {
        const json = JSON.stringify(res);
        sessionStorage.setItem(key, json);
    }

    public getDegradationCompounds(compoundCasNo: string, degradation: Degradation): string[] {
        let compoundChain = [compoundCasNo];
        if (degradation === Degradation.Sequential) {
            if (this.chloreredeEthener.indexOf(compoundCasNo) > -1) {
                compoundChain = this.chloreredeEthener;
            } else if (this.chloreredeEthaner.indexOf(compoundCasNo) > -1) {
                compoundChain = this.chloreredeEthaner;
            } else if (this.chloreredeMethaner.indexOf(compoundCasNo) > -1) {
                compoundChain = this.chloreredeMethaner;
            } else if (this.glyphosat.indexOf(compoundCasNo) > -1) {
                compoundChain = this.glyphosat;
            } else if (this.dichlobenil.indexOf(compoundCasNo) > -1) {
                compoundChain = this.dichlobenil;
            } else if (this.chloridazon.indexOf(compoundCasNo) > -1) {
                compoundChain = this.chloridazon;
            } else if (this.dichlorprop.indexOf(compoundCasNo) > -1) {
                compoundChain = this.dichlorprop;
            } else if (this.atrazin.indexOf(compoundCasNo) > -1) {
                compoundChain = this.atrazin;
            } else if (this.mtbe.indexOf(compoundCasNo) > -1) {
                compoundChain = this.mtbe;
            }
        }
        const compoundIndex = compoundChain.indexOf(compoundCasNo); // Will always be a proper index.

        const result = compoundChain.slice(compoundIndex);
        return result;
    }

    public getStoichiometricRatioList(compound: CalculationCompound, degradation: Degradation): number[] {
        let compoundChain = [compound.casNo];
        let stoichiometricRatios = [1];
        if (degradation === Degradation.Sequential) {
            if (this.chloreredeEthener.indexOf(compound.casNo) > -1) {
                compoundChain = this.chloreredeEthener;
                stoichiometricRatios = [1, 0.7924, 0.7377, 0.6447];
            } else if (this.chloreredeEthaner.indexOf(compound.casNo) > -1) {
                compoundChain = this.chloreredeEthaner;
                stoichiometricRatios = [1, 0.7418, 0.6520];
            } else if (this.chloreredeMethaner.indexOf(compound.casNo) > -1) {
                compoundChain = this.chloreredeMethaner;
                stoichiometricRatios = [1, 0.7761, 0.7114, 0.5945];
            } else if (this.glyphosat.indexOf(compound.casNo) > -1) {
                compoundChain = this.glyphosat;
                stoichiometricRatios = [1, 0.6570];
            } else if (this.dichlobenil.indexOf(compound.casNo) > -1) {
                compoundChain = this.dichlobenil;
                stoichiometricRatios = [1, 1.1064];
            } else if (this.chloridazon.indexOf(compound.casNo) > -1) {
                compoundChain = this.chloridazon;
                stoichiometricRatios = [1, 0.6657];
            } else if (this.dichlorprop.indexOf(compound.casNo) > -1) {
                compoundChain = this.dichlorprop;
                stoichiometricRatios = [1, 0.8533];
            } else if (this.atrazin.indexOf(compound.casNo) > -1) {
                compoundChain = this.atrazin;
                stoichiometricRatios = [1, 0.8699];
            } else if (this.mtbe.indexOf(compound.casNo) > -1) {
                compoundChain = this.mtbe;
                stoichiometricRatios = [1, 0.8408];
            }
        }
        const compoundIndex = compoundChain.indexOf(compound.casNo); // Will always be a proper index.
        // TODO: We can set first value to 1, but it does not matter for the models:
        const result = stoichiometricRatios.slice(compoundIndex);
        return result;
    }

    public isBenzenCompound(standat: number): boolean {
        const standatList = CalculationCompoundStandatMap.get(CalculationCompounds.Benzen);
        if (standatList == null || standatList.length === 0) {
            return false;
        }
        if (standatList.some(p => p === standat)) {
            return true;
        }
        return false;
    }

    public isToluenCompound(standat: number): boolean {
        const standatList = CalculationCompoundStandatMap.get(CalculationCompounds.Toluen);
        if (standatList == null || standatList.length === 0) {
            return false;
        }
        if (standatList.some(p => p === standat)) {
            return true;
        }
        return false;
    }

    public isEthylbenzenCompound(standat: number): boolean {
        const standatList = CalculationCompoundStandatMap.get(CalculationCompounds.Ethylbenzen);
        if (standatList == null || standatList.length === 0) {
            return false;
        }
        if (standatList.some(p => p === standat)) {
            return true;
        }
        return false;
    }

    public isXylenCompound(standat: number): boolean {
        const standatList = CalculationCompoundStandatMap.get(CalculationCompounds.Xylen);
        if (standatList == null || standatList.length === 0) {
            return false;
        }
        if (standatList.some(p => p === standat)) {
            return true;
        }
        return false;
    }

    public isNaphthalenCompound(standat: number): boolean {
        const standatList = CalculationCompoundStandatMap.get(CalculationCompounds.Naphthalen);
        if (standatList == null || standatList.length === 0) {
            return false;
        }
        if (standatList.some(p => p === standat)) {
            return true;
        }
        return false;
    }

    public isKulbrinterCompound(standat: number): boolean {
        const standatList = CalculationCompoundStandatMap.get(CalculationCompounds.Kulbrinter);
        if (standatList == null || standatList.length === 0) {
            return false;
        }
        if (standatList.some(p => p === standat)) {
            return true;
        }
        return false;
    }

    public getVerticalDegradation(standat: number): number | null {
        if (this.isBenzenCompound(standat)) {
            return 2;
        }
        if (this.isToluenCompound(standat)) {
            return 0.03;
        }
        if (this.isEthylbenzenCompound(standat)) {
            return 1;
        }
        if (this.isXylenCompound(standat)) {
            return 0.02;
        }
        if (this.isNaphthalenCompound(standat)) {
            return 0.3;
        }
        if (this.isKulbrinterCompound(standat)) {
            return 0.1;
        }
        return null;
    }
}
